import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GlassButton, GlassPanel, Segmented } from "@/components/glass/Glass";
import { IconGoogle, Logo } from "@/components/icons/Icons";
import { Page, Reveal } from "@/components/common/Reveal";
import { authService } from "@/services/userService";
import { useSeo } from "@/lib/seo";

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-semibold text-white/60">{label}</span>
      <input {...props} className="h-12 w-full rounded-[14px] border border-white/10 bg-white/[.05] px-4 text-[16px] font-medium text-white placeholder:text-white/30 transition-[border-color,background-color] focus:border-white/35 focus:bg-white/[.08] focus:outline-none" />
    </label>
  );
}

export function startGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/profile";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from && loc.state.from.startsWith("/") && !loc.state.from.startsWith("//") ? loc.state.from : "/profile";
  useSeo({ title: mode === "login" ? "Kirish" : "Ro‘yxatdan o‘tish" });

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "register" && form.password.length < 6) return setErr("Parol kamida 6 belgidan iborat bo‘lsin");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email.trim(), form.password);
      else await register(form.name.trim(), form.email.trim(), form.password);
      toast.success(mode === "login" ? "Xush kelibsiz!" : "Hisob yaratildi");
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Page className="pk-pb-dock relative min-h-screen overflow-hidden pt-[calc(80px+var(--safe-top))] lg:pt-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[480px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,.07),transparent)]" />
      <div className="pk-container relative max-w-[460px]">
        <Reveal>
          <GlassPanel className="rounded-[30px] p-6 sm:p-8" data-testid="auth-card">
            <div className="flex flex-col items-center text-center">
              <Logo />
              <h1 className="mt-5 text-[26px] font-extrabold tracking-[-0.02em] text-white">{mode === "login" ? "Hisobingizga kiring" : "Hisob yarating"}</h1>
              <p className="mt-1.5 text-[14px] text-white/55">Watchlist va tomosha tarixi barcha qurilmalarda.</p>
            </div>
            <div className="mt-6 flex justify-center">
              <Segmented options={[{ value: "login", label: "Kirish" }, { value: "register", label: "Ro‘yxatdan o‘tish" }]} value={mode} onChange={(v) => { setMode(v); setErr(""); }} testId="auth-mode" />
            </div>

            <GlassButton size="lg" className="mt-6 w-full bg-white/[.08]" onClick={startGoogleLogin} data-testid="google-login-button">
              <IconGoogle size={19} /> Google orqali davom etish
            </GlassButton>
            <div className="my-5 flex items-center gap-3 text-[12.5px] font-medium text-white/35">
              <span className="h-px flex-1 bg-white/10" /> yoki email bilan <span className="h-px flex-1 bg-white/10" />
            </div>

            <form onSubmit={submit} className="space-y-4" noValidate>
              {mode === "register" && <Field label="Ism" value={form.name} onChange={set("name")} autoComplete="name" required maxLength={60} placeholder="Ismingiz" data-testid="auth-name-input" />}
              <Field label="Email" type="email" value={form.email} onChange={set("email")} autoComplete="email" required placeholder="siz@misol.uz" data-testid="auth-email-input" />
              <Field label="Parol" type="password" value={form.password} onChange={set("password")} autoComplete={mode === "login" ? "current-password" : "new-password"} required minLength={6} placeholder="••••••" data-testid="auth-password-input" />
              {err && <p className="rounded-[12px] border border-[#FF453A]/25 bg-[#FF453A]/10 px-3 py-2 text-[13.5px] font-medium text-[#FF8A80]" role="alert" data-testid="auth-error">{err}</p>}
              <GlassButton type="submit" variant="primary" size="lg" className="w-full" disabled={busy} data-testid="auth-submit-button">
                {busy ? "Kuting…" : mode === "login" ? "Kirish" : "Ro‘yxatdan o‘tish"}
              </GlassButton>
            </form>
          </GlassPanel>
          <p className="mt-5 text-center text-[13px] text-white/40">
            <Link to="/" className="hover:text-white/70">Bosh sahifaga qaytish</Link>
          </p>
        </Reveal>
      </div>
    </Page>
  );
}

/** Handles the `#session_id=` fragment after Emergent Google sign-in. */
export function AuthCallback() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { setSessionUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const m = loc.hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : "";
    (async () => {
      try {
        const u = await authService.google(sid);
        setSessionUser(u);
        toast.success("Google orqali kirdingiz");
        navigate(loc.pathname || "/profile", { replace: true, state: { user: u } });
      } catch (e) {
        setSessionUser(null);
        toast.error(e.message || "Kirishda xatolik");
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div className="min-h-screen bg-[#070708]" data-testid="auth-callback" />;
}
